/**
 * EarlyWarningSignalsPage - Main orchestrator component
 *
 * Root component for the Early Warning Signals dashboard.
 * Manages state for tabs, filters, pagination, search, and drawer.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { RefreshCw, ChevronRight } from 'lucide-react';
import { TabSwitcher, DrawerProvider } from 'equal-ds-ui';
import { ReportDownloadDropdown } from '../../ui/ReportDownloadDropdown';
import { envService } from '../../../services/env.service';
import {
  EarlyWarningSignalsPageProps,
  EWSTab,
  SearchDomain,
  EWSCustomer,
  EWSOverviewMetrics,
  EWSDistributionsData,
  PaginationInfo,
  DistributionRiskFilter,
  RiskLevel,
  BarClickContext,
  CustomerSortColumn,
  SortOrder,
  GetDistributionsParams,
} from '../../../types/ews.types';
import { ewsApiService } from '../../../services/ews-api.service';
import { downloadAPI } from '../../../services/download-api.service';
import { DEFAULT_PAGE_SIZE } from '../../../constants/pagination.constants';
import { OverviewTab } from './OverviewTab';
import { CustomersTab } from './CustomersTab';
import { EWSCustomerDrawer } from './EWSCustomerDrawer';
import { EWSIndicatorCustomersDrawer } from './EWSIndicatorCustomersDrawer';

/**
 * Format timestamp to display format.
 */
function formatTimestamp(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).replace(',', '');
  } catch {
    return isoString;
  }
}

export const EarlyWarningSignalsPage: React.FC<EarlyWarningSignalsPageProps> = ({
  routeParams: _routeParams,
  queryParams,
}) => {
  // Tab state
  const [activeTab, setActiveTab] = useState<EWSTab>('overview');

  // The effective batchId: from query params or null (server uses default)
  const effectiveBatchId = queryParams?.batch_id || undefined;
  const referenceId = queryParams?.reference_id || undefined;

  // Overview state
  const [metrics, setMetrics] = useState<EWSOverviewMetrics | null>(null);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true);

  // Distributions state (for analytics section)
  const [distributions, setDistributions] = useState<EWSDistributionsData | null>(null);
  const [isLoadingDistributions, setIsLoadingDistributions] = useState(true);
  const [distributionRiskFilter, setDistributionRiskFilter] = useState<DistributionRiskFilter>('ALL');

  // Customers state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDomain, setSearchDomain] = useState<SearchDomain>('textualQuery');
  const [customers, setCustomers] = useState<EWSCustomer[] | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);

  // Sort state (for server-side sorting)
  const [sortBy, setSortBy] = useState<CustomerSortColumn | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Customer detail drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<EWSCustomer | null>(null);
  const [isLoadingCustomerDetail, setIsLoadingCustomerDetail] = useState(false);

  // Cache for customer details to avoid redundant API calls
  const customerDetailCache = useRef<Map<string, EWSCustomer>>(new Map());

  // Ref for download dropdown panel width
  const pageContentRef = useRef<HTMLDivElement>(null);

  // Download button visibility from DB config (userinfo.ui_config.common) with envService fallback
  const showDownloadButton = useMemo(() => {
    const encoded = sessionStorage.getItem('loggedUserInfo');
    const userInfo = encoded ? JSON.parse(atob(encoded)) : {};
    return userInfo?.ui_config?.common?.show_download_button_ews ?? envService.getUIConfig().common.show_download_button_ews;
  }, []);

  // Indicator customers drawer state (for bar click)
  const [isIndicatorDrawerOpen, setIsIndicatorDrawerOpen] = useState(false);
  const [indicatorContext, setIndicatorContext] = useState<BarClickContext | null>(null);

  // Refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Clear customer detail cache when batch changes
  useEffect(() => {
    customerDetailCache.current.clear();
  }, [effectiveBatchId]);

  /**
   * Load overview metrics.
   */
  const loadMetrics = useCallback(async () => {
    setIsLoadingMetrics(true);
    try {
      const data = await ewsApiService.getOverviewMetrics({
        batchId: effectiveBatchId,
      });
      setMetrics(data);
    } catch (error) {
      console.error('[EWS] Failed to load metrics:', error);
    } finally {
      setIsLoadingMetrics(false);
    }
  }, [effectiveBatchId]);

  /**
   * Load distribution data for analytics charts.
   */
  const loadDistributions = useCallback(async () => {
    setIsLoadingDistributions(true);
    try {
      const params: GetDistributionsParams = {
        batchId: effectiveBatchId,
      };
      // Only pass riskLevel if not ALL
      if (distributionRiskFilter !== 'ALL') {
        params.riskLevel = distributionRiskFilter;
      }
      const data = await ewsApiService.getDistributions(params);
      setDistributions(data);
    } catch (error) {
      console.error('[EWS] Failed to load distributions:', error);
    } finally {
      setIsLoadingDistributions(false);
    }
  }, [effectiveBatchId, distributionRiskFilter]);

  /**
   * Load customers list.
   * Note: API only supports searchQuery for consent ID search, not different search domains.
   */
  const loadCustomers = useCallback(async () => {
    setIsLoadingCustomers(true);
    try {
      const response = await ewsApiService.getCustomers({
        page: currentPage,
        pageSize,
        batchId: effectiveBatchId,
        searchQuery: searchQuery || undefined,
        sortBy: sortBy || undefined,
        sortOrder: sortBy ? sortOrder : undefined,
      });
      setCustomers(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error('[EWS] Failed to load customers:', error);
    } finally {
      setIsLoadingCustomers(false);
    }
  }, [currentPage, pageSize, searchQuery, effectiveBatchId, sortBy, sortOrder]);

  /**
   * Handle refresh button click.
   */
  const handleRefresh = async () => {
    setIsRefreshing(true);
    customerDetailCache.current.clear();
    try {
      const data = await ewsApiService.refreshMetrics(effectiveBatchId);
      setMetrics(data);
      // Also refresh distributions
      await loadDistributions();
      if (activeTab === 'customers') {
        await loadCustomers();
      }
    } catch (error) {
      console.error('[EWS] Failed to refresh:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  /**
   * Handle distribution risk filter change.
   */
  const handleDistributionRiskFilterChange = (filter: DistributionRiskFilter) => {
    setDistributionRiskFilter(filter);
  };

  /**
   * Handle search.
   * Only reset to page 1 if the query actually changed.
   */
  const handleSearch = useCallback((query: string, domain: SearchDomain) => {
    setSearchQuery(prevQuery => {
      // Only reset page if query actually changed
      if (prevQuery !== query) {
        setCurrentPage(1);
      }
      return query;
    });
    setSearchDomain(domain);
  }, []);

  /**
   * Handle page change.
   */
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  /**
   * Handle page size change.
   */
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page
  };

  /**
   * Handle sort change (server-side sorting).
   */
  const handleSortChange = (column: CustomerSortColumn | null, order: SortOrder) => {
    setSortBy(column);
    setSortOrder(order);
    setCurrentPage(1); // Reset to first page on sort change
  };

  /**
   * Handle customer row click.
   * Fetches full customer details from the API since list endpoint returns limited fields.
   */
  const handleCustomerClick = async (customer: EWSCustomer) => {
    const trackingId = customer.trackingId || customer.consentId;
    const cacheKey = trackingId ? `${trackingId}_${effectiveBatchId || ''}` : '';

    // Check cache first
    if (cacheKey && customerDetailCache.current.has(cacheKey)) {
      setSelectedCustomer(customerDetailCache.current.get(cacheKey)!);
      setIsDrawerOpen(true);
      return;
    }

    // Open drawer immediately with basic info from list
    setSelectedCustomer(customer);
    setIsDrawerOpen(true);
    setIsLoadingCustomerDetail(true);

    try {
      if (trackingId) {
        const details = await ewsApiService.getCustomerDetails(trackingId, effectiveBatchId);
        if (details) {
          setSelectedCustomer(details);
          customerDetailCache.current.set(cacheKey, details);
        }
      }
    } catch (error) {
      console.error('[EWS] Failed to load customer details:', error);
    } finally {
      setIsLoadingCustomerDetail(false);
    }
  };

  /**
   * Handle customer detail drawer close.
   * Also clears indicator context so back button won't appear incorrectly next time.
   */
  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedCustomer(null);
    setIndicatorContext(null); // Clear indicator context on full close
  };

  /**
   * Handle bar click in analytics charts.
   * Opens the indicator customers drawer.
   */
  const handleBarClick = (
    indicator: string,
    indicatorName: string,
    level: RiskLevel,
    levelLabel: string,
    count: number
  ) => {
    setIndicatorContext({ indicator, indicatorName, level, levelLabel, count });
    setIsIndicatorDrawerOpen(true);
  };

  /**
   * Handle indicator drawer close.
   */
  const handleIndicatorDrawerClose = () => {
    setIsIndicatorDrawerOpen(false);
    setIndicatorContext(null);
  };

  /**
   * Handle customer click in indicator drawer.
   * Closes indicator drawer and opens customer detail drawer.
   * Preserves indicator context so user can go back.
   */
  const handleIndicatorCustomerClick = async (customer: EWSCustomer) => {
    const trackingId = customer.trackingId || customer.consentId;
    const cacheKey = trackingId ? `${trackingId}_${effectiveBatchId || ''}` : '';

    // Close indicator drawer first (but keep indicatorContext for back navigation)
    setIsIndicatorDrawerOpen(false);

    // Check cache first
    if (cacheKey && customerDetailCache.current.has(cacheKey)) {
      setSelectedCustomer(customerDetailCache.current.get(cacheKey)!);
      setIsDrawerOpen(true);
      return;
    }

    // Then open customer detail drawer
    setSelectedCustomer(customer);
    setIsDrawerOpen(true);
    setIsLoadingCustomerDetail(true);

    try {
      if (trackingId) {
        const details = await ewsApiService.getCustomerDetails(trackingId, effectiveBatchId);
        if (details) {
          setSelectedCustomer(details);
          customerDetailCache.current.set(cacheKey, details);
        }
      }
    } catch (error) {
      console.error('[EWS] Failed to load customer details:', error);
    } finally {
      setIsLoadingCustomerDetail(false);
    }
  };

  /**
   * Handle back from customer detail to indicator drawer.
   */
  const handleBackToIndicatorDrawer = () => {
    // Close customer drawer
    setIsDrawerOpen(false);
    setSelectedCustomer(null);
    // Reopen indicator drawer
    setIsIndicatorDrawerOpen(true);
  };

  /**
   * Handle tab change.
   */
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId as EWSTab);
  };


  // Load metrics and distributions
  useEffect(() => {
    loadMetrics();
    loadDistributions();
  }, [loadMetrics, loadDistributions]);

  // Load customers when tab becomes active or filters change
  useEffect(() => {
    if (activeTab === 'customers') {
      loadCustomers();
    }
  }, [activeTab, loadCustomers]);

  // Dashboard view
  return (
    <DrawerProvider side="right" open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
      <div ref={pageContentRef} className="h-full flex flex-col">
        <div className="space-y-6">
          {/* Header */}
          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold text-text-primary tracking-wide">
                  Early Warning Signals
                </h1>
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                  title="Refresh data"
                >
                  <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
                {metrics?.lastRefreshedAt && (
                  <span className="text-xs text-gray-400">
                    (Last refreshed on {formatTimestamp(metrics.lastRefreshedAt)})
                  </span>
                )}
              </div>
            </div>
            <p className="text-sm text-text-secondary mt-1">
              View/manage different user cohorts
            </p>
          </div>

          {/* Breadcrumb + Download */}
          <div className="flex items-center justify-between">
            <nav style={{ display: 'flex', alignItems: 'center', fontSize: '14px', gap: '6px' }}>
              <span
                onClick={() => { window.location.hash = '#/dashboard/bulkDataFetch?type=loan_monitoring'; }}
                style={{ color: '#6b7280', cursor: 'pointer' }}
              >
                Batches
              </span>
              {(referenceId || effectiveBatchId) && (
                <>
                  <ChevronRight style={{ width: '16px', height: '16px', color: '#9ca3af' }} />
                  <span style={{ color: '#111827', fontWeight: 600 }}>
                    {(() => {
                      const label = referenceId || effectiveBatchId || '';
                      return label.length > 24 ? `${label.slice(0, 24)}...` : label;
                    })()}
                  </span>
                </>
              )}
            </nav>

            {showDownloadButton && (
              <ReportDownloadDropdown
                buttonLabel="Download Data"
                buttonVariant="primary"
                reportTypes={[
                  { value: 'ews', title: 'EWS Report', description: 'Single report with balance information for all records' },
                  { value: 'raw', title: 'Raw Data', description: 'Single report with balance information for all records' },
                ]}
                defaultReportType="ews"
                formats={[{ value: 'csv', label: '.csv' }]}
                defaultFormat="csv"
                onGenerate={async ({ reportType, format }) => {
                  const isJobBasedDownloadEnabled = sessionStorage.getItem('isJobBasedDownloadEnabled') === 'true';
                  if (isJobBasedDownloadEnabled && effectiveBatchId) {
                    const downloadType = reportType === 'ews' ? 'EWS_REPORT' : 'FI_RAW_DATA';
                    await downloadAPI.createBatchJob(effectiveBatchId, downloadType);
                    return null;
                  }
                  return ewsApiService.exportReport({
                    period: 'lastYear', // Backend requires period param; time-period UI was removed, defaulting to 'lastYear' for full dataset
                    reportType: reportType as 'ews' | 'raw',
                    format: format as 'csv',
                    batchId: effectiveBatchId,
                  });
                }}
                getFileName={({ reportType, format }) => {
                  const label = reportType === 'ews' ? 'ews-report' : 'raw-data';
                  const suffix = effectiveBatchId ? effectiveBatchId.slice(0, 8) : 'all';
                  return `${label}-${suffix}.${format}`;
                }}
                postGenerateAction={{
                  label: 'GO TO DOWNLOAD CENTER',
                  onClick: () => {
                    if ((window as any).AngularAuthBridge?.navigate) {
                      (window as any).AngularAuthBridge.navigate('/dashboard/download-centre');
                    } else {
                      window.location.href = '/dashboard/download-centre';
                    }
                  },
                }}
                panelContainerRef={pageContentRef}
              />
            )}
          </div>

          {/* Tab Switcher - no wrapper background */}
          <TabSwitcher
            tabs={[
              { id: 'overview', label: 'Overview' },
              { id: 'customers', label: 'Customers' },
            ]}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            variant="horizontal"
          />

          {/* Tab Content - flex-1 to fill remaining space */}
          <div className="flex-1 min-h-0 flex flex-col">
            {activeTab === 'overview' ? (
              <OverviewTab
                metrics={metrics}
                isLoading={isLoadingMetrics}
                distributions={distributions}
                isLoadingDistributions={isLoadingDistributions}
                selectedRiskFilter={distributionRiskFilter}
                onRiskFilterChange={handleDistributionRiskFilterChange}
                onBarClick={handleBarClick}
              />
            ) : (
              <CustomersTab
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                searchQuery={searchQuery}
                searchDomain={searchDomain}
                onSearch={handleSearch}
                customers={customers}
                pagination={pagination}
                isLoading={isLoadingCustomers}
                onCustomerClick={handleCustomerClick}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
              />
            )}
          </div>
        </div>

        {/* Customer Detail Drawer */}
        <EWSCustomerDrawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          customer={selectedCustomer}
          isLoading={isLoadingCustomerDetail}
          onBack={indicatorContext ? handleBackToIndicatorDrawer : undefined}
        />

        {/* Indicator Customers Drawer (opened from bar click) */}
        {indicatorContext && (
          <EWSIndicatorCustomersDrawer
            isOpen={isIndicatorDrawerOpen}
            onClose={handleIndicatorDrawerClose}
            indicator={indicatorContext.indicator}
            indicatorName={indicatorContext.indicatorName}
            level={indicatorContext.level}
            levelLabel={indicatorContext.levelLabel}
            totalCount={indicatorContext.count}
            batchId={effectiveBatchId}
            onCustomerClick={handleIndicatorCustomerClick}
          />
        )}
      </div>
    </DrawerProvider>
  );
};

export default EarlyWarningSignalsPage;
